import React, { type JSX, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@core-ui/Components/ui/dialog.tsx';
import { Input } from '@core-ui/Components/ui/input.tsx';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { cn } from '@core-ui-lib/utils.ts';
import useCallerIdNumbersEdgeApi from '@features/numbers/hooks/useCallerIdNumbers.Api.ts';
import { getCurrentUser } from '@features/auth';
import { useTranslation } from 'react-i18next';
import { IconInfoCircle } from '@tabler/icons-react';
import { Label } from '@core-ui/Components/ui/label.tsx';

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  onSave: () => void;
  errors?: Partial<Record<
    'number',
    string
  >>;
}

export function CallerIdNewNumber ({ setOpen, open, errors, onSave }: Props): React.JSX.Element {
  const { validateNumberAsync, registryCallerId } = useCallerIdNumbersEdgeApi();
  const currentUser = getCurrentUser();
  const { t } = useTranslation();

  const errorText = (field: keyof NonNullable<Props['errors']>): JSX.Element | null =>
    ((errors?.[field]) != null) ? (
      <p data-qa-id={`error-${field}`} className="mt-1 text-xs text-destructive">{errors[field]}</p>
    ) : null;

  const [calling, setCalling] = useState(false);
  const [validationCode, setValidationCode] = useState('');
  const [newNumber, setNumberInput] = useState('');
  const [validationError, setValidationError] = useState('');

  function handleClose (): void {
    setValidationCode('');
    setCalling(false);
    setNumberInput('');
    setValidationError('');
    setOpen(false);
  }

  async function handleNumberCall (): Promise<void> {
    setValidationError('');
    const checkDuplicate = await validateNumberAsync(
      newNumber,
      currentUser?.userId ?? ''
    );
    if (!checkDuplicate.success) {
      const errorMessage = checkDuplicate.error?.message ?? t('settings.callerId.newCallerId.callError');
      setValidationError(errorMessage);
      return;
    }

    if (checkDuplicate.data === true) {
      setValidationError(t('settings.callerId.newCallerId.callerError'));
      return;
    }
    const registryRes = await registryCallerId(
      newNumber,
      currentUser?.userId ?? ''
    );

    if (!registryRes.success) {
      const rawErrorObj = registryRes.error?.raw as Record<string, unknown> | null | undefined;
      const apiMessage = (rawErrorObj !== null && rawErrorObj !== undefined && typeof rawErrorObj.message === 'string')
        ? rawErrorObj.message
        : undefined;

      const errorMessage: string = (apiMessage !== undefined && apiMessage !== '')
        ? String(apiMessage)
        : t('settings.callerId.newCallerId.callerError');
      setValidationError(errorMessage);
      return;
    }

    const code = registryRes.data;

    if (code !== undefined && code !== null) {
      setValidationCode(code);
      setCalling(true);
    }

    let timesRun = 0;
    const intervalId = setInterval(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async function () {
        timesRun += 1;

        const resValidation = await validateNumberAsync(
          newNumber,
          currentUser?.userId ?? ''
        );

        if (!resValidation.success) {
          clearInterval(intervalId);
          setCalling(false);
          const errorMessage = resValidation.error?.message ?? 'Connection error';
          setValidationError(errorMessage);
          return;
        }

        const validation = resValidation.data;

        if (validation !== undefined && validation) {
          clearInterval(intervalId);
          setCalling(false);
          onSave();
          setOpen(false);
          setValidationCode('');
          setValidationError('');
          return;
        }

        if (timesRun >= 12) {
          clearInterval(intervalId);
          setCalling(false);
          setValidationCode('');
          setNumberInput('');
          setValidationError(`${t('settings.callerId.newCallerId.timeoutError')}`);
        }
      }, 5000);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose();
        } else {
          setOpen(isOpen);
        }
      }}
      modal={true}
    >
      <DialogContent
        className={cn(
          'rounded-2xl shadow-2xl w-full max-w-lg',
          'bg-card text-foreground border border-border',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          '[&_div[data-radix-dialog-overlay]]:bg-black/60',
          '[&_div[data-radix-dialog-overlay]]:backdrop-blur-sm'
        )}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-left">
            {t('settings.callerId.newCallerId.title', 'Add new Caller ID number')}
          </DialogTitle>

          <div className="p-2 w-full rounded-lg bg-muted border border-border flex flex-col md:flex-row items-center gap-3">
            <IconInfoCircle className="min-w-[20px] min-h-[20px]" />
            <p className="text-muted-foreground text-sm">
              {t('settings.callerId.newCallerId.description1')}
              <span className="text-muted-foreground text-sm font-bold">
                {t('settings.callerId.newCallerId.callMe')}
              </span>
              {t('settings.callerId.newCallerId.description2')}
            </p>
          </div>
        </DialogHeader>

        <div
          className={cn(
            (validationCode !== '') ? 'flex flex-row justify-between' : ''
          )}
        >
          {/* Phone Number */}
          <div
            className={cn(
              'col-span-2',
              (validationCode !== '') ? 'grid-cols-1' : ''
            )}
          >
            <label className="text-sm font-medium block mb-1 text-muted-foreground">
              {t('settings.callerId.newCallerId.input', 'Phone Number')}
            </label>
            <Input
              onChange={(e) => {
                setNumberInput(e.target.value);
              }}
              data-qa-id="number-input"
              placeholder="+512121212121"
              aria-invalid={!(errors?.number == null)}
              className={cn(
                'bg-background placeholder:text-muted-foreground ',
                'border',
                errors?.number != null
                  ? 'border-destructive focus-visible:ring-destructive/40'
                  : 'border-border'
              )}
            />
            {errorText('number')}
            {validationError !== '' && (
              <Label className="mt-1 text-xs font-medium block text-destructive">
                {validationError}
              </Label>
            )}
          </div>

          {validationCode !== '' && (
            <div >
              <label className="text-sm font-medium block mb-1 text-muted-foreground">
                {t('settings.callerId.newCallerId.code', 'Validation code')}
              </label>

              <p data-qa-id="validation-code" className="text-3xl font-semibold tracking-widest">
                {validationCode}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end mt-6 gap-4">
          <Button
            data-qa-id="cancel-callerId-button"
            variant="outline"
            onClick={() => {
              handleClose();
            }}
            className="border border-border text-foreground hover:bg-muted"
          >
            {t('settings.callerId.newCallerId.cancel', 'Cancel')}
          </Button>
          <Button
            data-qa-id="call-me-button"
            onClick={() => {
              void handleNumberCall();
            }}
            className={cn(
              'bg-primary text-primary-foreground hover:bg-primary/90',
            )}
            disabled={calling || newNumber === ''}
          >
            {calling ? t('settings.callerId.newCallerId.calling', 'Calling...') : t('settings.callerId.newCallerId.callMe', 'Call Me')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}